import {DestroyRef, Injectable, NgZone, inject} from '@angular/core';
import {SwUpdate} from '@angular/service-worker';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ConfirmationService, MessageService} from 'primeng/api';
import {timer} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly checkEveryMs = 60 * 60 * 1000;
  private updatePromptVisible = false;

  constructor(
    private swUpdate: SwUpdate,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private zone: NgZone,
  ) {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.watchForAvailableUpdates();
    this.watchForUnrecoverableState();
    this.checkForUpdates();
  }

  private watchForAvailableUpdates() {
    this.swUpdate.versionUpdates
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.type === 'VERSION_READY') {
          this.showUpdatePrompt();
        }

        if (event.type === 'VERSION_INSTALLATION_FAILED') {
          this.messageService.add({
            severity: 'warn',
            summary: 'Update failed',
            detail: 'The latest app update could not be downloaded. Please try refreshing again later.',
            closable: true
          });
        }
      });
  }

  private watchForUnrecoverableState() {
    this.swUpdate.unrecoverable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.zone.run(() => {
          this.confirmationService.confirm({
            header: 'Reload required',
            message: 'The app needs to reload to continue working correctly.',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Reload',
            rejectVisible: false,
            closable: false,
            closeOnEscape: false,
            accept: () => window.location.reload()
          });
        });
      });
  }

  private checkForUpdates() {
    timer(60 * 1000, this.checkEveryMs)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.swUpdate.checkForUpdate().catch(() => {
          this.messageService.add({
            severity: 'warn',
            summary: 'Update check failed',
            detail: 'Could not check for a new app version. Please try again later.',
            closable: true
          });
        });
      });
  }

  private showUpdatePrompt() {
    if (this.updatePromptVisible) {
      return;
    }

    this.updatePromptVisible = true;

    this.zone.run(() => {
      this.confirmationService.confirm({
        header: 'Update available',
        message: 'A new version of the app is ready. Update now to get the latest changes.',
        icon: 'pi pi-refresh',
        acceptLabel: 'Update now',
        rejectLabel: 'Later',
        acceptButtonStyleClass: 'p-button-success',
        rejectButtonStyleClass: 'p-button-text',
        accept: () => this.activateUpdate(),
        reject: () => {
          this.updatePromptVisible = false;
        }
      });
    });
  }

  private activateUpdate() {
    this.swUpdate.activateUpdate()
      .then(() => window.location.reload())
      .catch(() => {
        this.updatePromptVisible = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Update failed',
          detail: 'Could not apply the latest app update. Please refresh the page manually.',
          sticky: true,
          closable: true
        });
      });
  }
}
